import { buildSystemDefaults, INITIAL_DELAY_UNITS, RETRY_AGE_UNITS } from "./punchProcessingRules";

// GET reads the single system record. PUT updates it by the id GET returned.
export const systemPunchProcessingUrl = (domain, id) =>
  `${domain}monitoring/punch-processing/system/${id == null ? "" : `${id}/`}`;

const duration = (value, unit) => ({ value: String(value), unit });
const RETRY_GROUPS = [["dispatcherRetry", "dispatcher_retry"], ["attendanceRetry", "attendance_processing_retry"]];
const RETENTION_FIELDS = {
  hot: "global_event_hot_retention_days",
  archive: "global_event_archive_retention_days",
  rawPayload: "raw_payload_retention_days",
  technicalAudit: "technical_audit_retention_days",
};
const MONITORING_FIELDS = {
  deviceInactivity: "device_inactivity_warning_minutes",
  deviceOffline: "device_offline_threshold_minutes",
  clockDrift: "clock_drift_warning_minutes",
  eventFlowInactivity: "event_flow_inactivity_warning_minutes",
};

const unitList = (options, fallback) => (Array.isArray(options) && options.length ? options : fallback);

// The unit choices the server allows for the two retry fields that carry a unit.
export const systemUnitOptionsFromApi = (data = {}) => ({
  initialDelay: unitList(data.initial_retry_delay_unit_options, INITIAL_DELAY_UNITS),
  maxAge: unitList(data.maximum_retry_age_unit_options, RETRY_AGE_UNITS),
});

export const systemSettingsFromApi = data => {
  const form = buildSystemDefaults();
  const retryRecovery = data.retry_recovery || {};
  RETRY_GROUPS.forEach(([key, apiKey]) => {
    const retry = retryRecovery[apiKey] || {};
    form[key] = {
      maxAttempts: String(retry.maximum_attempts),
      initialDelay: duration(retry.initial_retry_delay, retry.initial_retry_delay_unit),
      maxDelay: duration(retry.maximum_retry_delay_minutes, "minutes"),
      maxAge: duration(retry.maximum_retry_age, retry.maximum_retry_age_unit),
    };
  });
  form.quarantineAfterRetryLimit = retryRecovery.move_events_to_quarantine_after_retry_limit ?? true;
  Object.entries(RETENTION_FIELDS).forEach(([key, apiKey]) => {
    form.retention[key] = duration(data.data_retention?.[apiKey], "days");
  });
  Object.entries(MONITORING_FIELDS).forEach(([key, apiKey]) => {
    form.monitoring[key] = duration(data.device_monitoring?.[apiKey], "minutes");
  });
  return form;
};

const numbers = (fields, values) => Object.fromEntries(
  Object.entries(fields).map(([key, apiKey]) => [apiKey, Number(values[key].value)]),
);

export const systemSettingsToApi = form => {
  const retryRecovery = {};
  RETRY_GROUPS.forEach(([key, apiKey]) => {
    retryRecovery[apiKey] = {
      maximum_attempts: Number(form[key].maxAttempts),
      initial_retry_delay: Number(form[key].initialDelay.value),
      initial_retry_delay_unit: form[key].initialDelay.unit,
      maximum_retry_delay_minutes: Number(form[key].maxDelay.value),
      maximum_retry_age: Number(form[key].maxAge.value),
      maximum_retry_age_unit: form[key].maxAge.unit,
    };
  });
  retryRecovery.move_events_to_quarantine_after_retry_limit = form.quarantineAfterRetryLimit;
  return {
    retry_recovery: retryRecovery,
    data_retention: numbers(RETENTION_FIELDS, form.retention),
    device_monitoring: numbers(MONITORING_FIELDS, form.monitoring),
  };
};
